/**
 * storage.js — Capa de acceso a datos (Supabase)
 *
 * Funciones:
 *   Bots:  listBots, getBot, createBot, updateBot, deleteBot
 *   Flujo: saveFlow (guarda data de nodos dentro del bot)
 */
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL      = "https://ubqwzzxbhvdsairrxhsw.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_LsufqZX5suRCd1_F5B-48Q_WaKgRki5";

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const DEFAULT_FLOW = {
  nodes: [{ id: "start_1", type: "startingPoint", x: 100, y: 280, data: {} }],
  edges: [],
  vars: [
    { id: "v1", name: "nombre",   defaultValue: "", value: "" },
    { id: "v2", name: "telefono", defaultValue: "", value: "" },
  ],
  lang: "es",
};

// ============================================================
// BOTS
// ============================================================

/** Lista todos los bots del usuario */
export async function listBots(userId) {
  const { data, error } = await supabase
    .from("bots")
    .select("id, name, phone, created_at, updated_at")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
}

/** Obtiene un bot completo (incluyendo data del flujo) */
export async function getBot(botId) {
  const { data, error } = await supabase
    .from("bots")
    .select("*")
    .eq("id", botId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
}

/** Crea un bot nuevo para el usuario */
export async function createBot(userId, name, phone = "") {
  const id = `bot_${Date.now().toString(36)}`;
  const { data, error } = await supabase
    .from("bots")
    .insert({
      id,
      user_id: userId,
      name,
      phone,
      data: DEFAULT_FLOW,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/** Actualiza nombre y/o teléfono de un bot */
export async function updateBotMeta(botId, { name, phone }) {
  const { error } = await supabase
    .from("bots")
    .update({ name, phone, updated_at: new Date().toISOString() })
    .eq("id", botId);

  if (error) throw new Error(error.message);
}

/** Guarda el flujo (nodos/edges/vars) de un bot */
export async function saveFlow(botId, flowData) {
  const { error } = await supabase
    .from("bots")
    .update({ data: flowData, updated_at: new Date().toISOString() })
    .eq("id", botId);

  if (error) throw new Error(error.message);
}

/** Duplica un bot existente con nuevo nombre y teléfono */
export async function duplicateBot(userId, sourceId, name, phone = "") {
  // Carga el bot original completo
  const { data: source, error: fetchError } = await supabase
    .from("bots")
    .select("*")
    .eq("id", sourceId)
    .maybeSingle();

  if (fetchError) throw new Error(fetchError.message);
  if (!source)    throw new Error("Bot original no encontrado");

  const id = `bot_${Date.now().toString(36)}`;
  const { data, error } = await supabase
    .from("bots")
    .insert({
      id,
      user_id: userId,
      name,
      phone,
      data: source.data, // copia el flujo completo
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/** Elimina un bot */
export async function deleteBot(botId) {
  const { error } = await supabase
    .from("bots")
    .delete()
    .eq("id", botId);

  if (error) throw new Error(error.message);
}
